<template>
  <div style="max-width: 450px; margin: 0 auto;">
    <h4 class="text-center">Visits by Web Browser</h4>
    <Doughnut v-if="chartData" :data="chartData" :options="chartOptions" />
    <div v-else>Loading...</div>
  </div>
</template>

<script setup>
/**
 * WebChartComponent
 *
 * This Vue component displays a Doughnut chart with visits grouped by web browser.
 * It uses vue-chartjs and chart.js to render the chart and axios to fetch data from the API.
 *
 * @module WebChartComponent
 */

import { ref, onMounted } from 'vue';
import { Doughnut } from 'vue-chartjs';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import axios from 'axios';

// Register required Chart.js components
Chart.register(ArcElement, Tooltip, Legend);

/**
 * Reactive data for the chart.
 * @type {import('vue').Ref<Object|null>}
 */
const chartData = ref(null);

/**
 * Configuration options for the Doughnut chart.
 * @type {Object}
 */
const chartOptions = {
  responsive: true,
  plugins: {
    legend: { display: true, position: 'bottom' },
    title: { display: true, text: 'Visits by Web Browser' }
  }
};

/**
 * Predefined colors for the chart sections.
 * @type {string[]}
 */
const colors = [
  '#fd7e14', '#ffc107', '#198754', '#20c997', '#0dcaf0'
];

/**
 * Lifecycle hook executed when the component is mounted.
 * Fetches visit data by browser from the API and processes it for the chart.
 * If an error occurs or no valid data is found, initializes an empty chart.
 *
 * @function
 * @async
 */
onMounted(/**
 * Fetches and processes user link visit data from an API endpoint to generate a chart dataset.
 *
 * Retrieves user ID and authentication token from session storage and makes a GET request to the
 * API to fetch data about the links visited by the user. Processes the data to count visits
 * grouped by web browser, and structures the data for use in chart visualization. In case of an
 * error or invalid response, initializes an empty dataset.
 *
 * API URL and token:
 * - The API base URL is retrieved from the environment variable `VUE_APP_API_URL`.
 * - The authentication token is retrieved from session storage under the key `token`.
 *
 * API data structure:
 * - Expected response is an object containing a property `linksVisited`, which is an array
 *   of objects. Each object should have a `web_navigator` property indicating the name of
 *   the web browser used to visit a link.
 *
 * Chart dataset:
 * - Labels correspond to unique browser names extracted from the response data.
 * - Data represents the number of visits per browser.
 * - Background colors for the chart are determined by cycling through a pre-defined array
 *   of colors (`colors`).
 *
 * Error scenarios:
 * - On network failure, invalid token, or API errors, the chart dataset is reset to an empty state.
 *
 * Dependencies:
 * - Uses the `axios` library for making HTTP requests.
 * - Relies on `chartData` and `colors` as external reactive variables or constants.
 */
async () => {
  const apiUrl = process.env.VUE_APP_API_URL;
  const token = sessionStorage.getItem('token');

  try {
    const id = sessionStorage.getItem('id');
    const res = await axios.get(`${apiUrl}/linksvisited/id_user/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (res && Array.isArray(res.data.linksVisited)) {
      /** @type {Object.<string, number>} */
      const counts = {};
      res.data.linksVisited.forEach(item => {
        const browser = item?.web_navigator || 'Unknown';
        counts[browser] = (counts[browser] || 0) + 1;
      });
      const labels = Object.keys(counts);
      chartData.value = {
        labels,
        datasets: [{
          label: 'Visits',
          backgroundColor: labels.map((_, i) => colors[i % colors.length]),
          data: Object.values(counts)
        }]
      };
    } else {
      chartData.value = {
        labels: [],
        datasets: [{ label: 'Visits', data: [] }]
      };
    }
  } catch (e) {
    chartData.value = {
      labels: [],
      datasets: [{ label: 'Visits', data: [] }]
    };
  }
});
</script>
